import { useState, useEffect, useCallback, useRef } from 'react';
import { storeSettingsApi, appConfigApi } from '../utils/api';

const STORAGE_KEY = 'twigs_settings';

// Per-browser preferences (localStorage). Older browsers may also hold
// salesTaxRate / priorityTimeFeeAmount / careInstructions here from before
// those became shop-wide; the shared values below always win over them.
const DEFAULT_SETTINGS = {
  showSearchHelp: true,
  timezone: 'America/New_York',
  orderViewMode: 'table', // 'cards' or 'table'
  showLowStockWarnings: true // Show low inventory warnings when adding products
};

// Shop-wide values that change what an order charges. They live in the DB
// (store_settings, admin-only writes) so every browser charges the same.
// The fee toggle defaults on: if the API is unreachable the fee should still
// auto-apply rather than silently drop off. The sales tax rate has no default:
// null means the shop has not set it, so orders charge 0 and the order form
// says so. storeState is the shop's own state (from its address), read-only
// here, used as the recipient's default state.
export const SHARED_DEFAULTS = {
  priorityTimeFeeEnabled: true,
  priorityTimeFeeAmount: 10.00,
  salesTaxRate: null,
  // 'stripe': the shop opted into Stripe Tax, so the order forms ask the
  // server for the tax (POST /tax/quote); 'flat' taxes at salesTaxRate.
  salesTaxMode: 'flat',
  onlineStoreInNav: true, // Online store section in the sidebar
  abandonedCartEmails: true, // Online store 5B: one email when a shopper's cart goes quiet
  occasionReminders: true, // Online store 5B: one email before a date a shopper saved
  // The cash register (Register in the sidebar, cash and check in Take
  // payment). Off until the server says (a missing row is on there; Twigs
  // is off), so a shop without it never sees the entry flash.
  cashRegisterEnabled: false,
  storeState: null
};
export const SHARED_SETTING_KEYS = {
  priorityTimeFeeEnabled: 'priority_time_fee_enabled',
  priorityTimeFeeAmount: 'priority_time_fee_amount',
  salesTaxRate: 'sales_tax_rate',
  onlineStoreInNav: 'online_store_in_nav',
  abandonedCartEmails: 'abandoned_cart_emails',
  occasionReminders: 'occasion_reminders',
  cashRegisterEnabled: 'cash_register_enabled'
};

// Last values read from the server, so a page that loads while the API is
// down still charges the shop's rate instead of the built-in default.
const SHARED_CACHE_KEY = 'twigs_shared_settings';

const readSharedCache = () => {
  try {
    return { ...SHARED_DEFAULTS, ...JSON.parse(localStorage.getItem(SHARED_CACHE_KEY) || '{}') };
  } catch (error) {
    return { ...SHARED_DEFAULTS };
  }
};

const writeSharedCache = (shared) => {
  try {
    localStorage.setItem(SHARED_CACHE_KEY, JSON.stringify(shared));
  } catch (error) {
    // Storage full or blocked — the in-memory value still applies.
  }
};

export const useSettings = () => {
  const [localSettings, setLocalSettings] = useState(DEFAULT_SETTINGS);
  const [sharedSettings, setSharedSettings] = useState(readSharedCache);
  // Mirrors sharedSettings synchronously so back-to-back saves build on each other.
  const sharedRef = useRef(sharedSettings);
  const setShared = useCallback((next) => {
    sharedRef.current = next;
    setSharedSettings(next);
  }, []);
  const [loading, setLoading] = useState(true);

  // Load local settings from localStorage
  const loadSettings = () => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setLocalSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } else {
        setLocalSettings(DEFAULT_SETTINGS);
      }
    } catch (error) {
      setLocalSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  // Sync the timezone from the server (DB store_settings.timezone, with the
  // APP_TIMEZONE env var as fallback). The DB is the source of truth so the
  // store timezone is a shared account setting rather than per-browser: we
  // mirror it into localStorage — which dateUtils reads synchronously while
  // formatting — and broadcast so every consumer re-renders.
  const refreshAppConfig = useCallback(async () => {
    try {
      const config = await appConfigApi.get();
      if (!config?.appTimezone) return;

      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : {};
      if (parsed.timezone === config.appTimezone) return; // already in sync

      const next = { ...DEFAULT_SETTINGS, ...parsed, timezone: config.appTimezone };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setLocalSettings(next);
      window.dispatchEvent(new CustomEvent('settingsChanged', { detail: next }));
    } catch (error) {
      // Network error — fall back to whatever localStorage / DEFAULT_SETTINGS has.
      console.warn('[useSettings] Could not fetch app config:', error.message);
    }
  }, []);

  const applySharedSettings = useCallback((next) => {
    setShared(next);
    writeSharedCache(next);
    window.dispatchEvent(new CustomEvent('sharedSettingsChanged', { detail: next }));
  }, [setShared]);

  // Fetch the shop-wide settings from the public read endpoint. A key the
  // shop has never saved comes back null and gets the built-in default.
  const refreshGlobalSettings = useCallback(async () => {
    try {
      const data = (await storeSettingsApi.getPublic())?.data || {};
      const next = {
        priorityTimeFeeEnabled: typeof data.priority_time_fee_enabled === 'boolean'
          ? data.priority_time_fee_enabled
          : SHARED_DEFAULTS.priorityTimeFeeEnabled,
        priorityTimeFeeAmount: typeof data.priority_time_fee_amount === 'number'
          ? data.priority_time_fee_amount
          : SHARED_DEFAULTS.priorityTimeFeeAmount,
        salesTaxRate: typeof data.sales_tax_rate === 'number'
          ? data.sales_tax_rate
          : SHARED_DEFAULTS.salesTaxRate,
        salesTaxMode: data.sales_tax_mode === 'stripe' ? 'stripe' : SHARED_DEFAULTS.salesTaxMode,
        onlineStoreInNav: typeof data.online_store_in_nav === 'boolean'
          ? data.online_store_in_nav
          : SHARED_DEFAULTS.onlineStoreInNav,
        abandonedCartEmails: typeof data.abandoned_cart_emails === 'boolean'
          ? data.abandoned_cart_emails
          : SHARED_DEFAULTS.abandonedCartEmails,
        occasionReminders: typeof data.occasion_reminders === 'boolean'
          ? data.occasion_reminders
          : SHARED_DEFAULTS.occasionReminders,
        cashRegisterEnabled: typeof data.cash_register_enabled === 'boolean'
          ? data.cash_register_enabled
          : SHARED_DEFAULTS.cashRegisterEnabled,
        storeState: typeof data.store_state === 'string' && data.store_state
          ? data.store_state
          : SHARED_DEFAULTS.storeState
      };
      setShared(next);
      writeSharedCache(next);
    } catch (error) {
      // Network error — keep the last values read from the server.
      console.warn('[useSettings] Could not fetch global settings, keeping last known:', error.message);
    }
  }, [setShared]);

  // Save local-only settings to localStorage
  const saveSettings = (newSettings) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      setLocalSettings(newSettings);
      window.dispatchEvent(new CustomEvent('settingsChanged', { detail: newSettings }));
      return true;
    } catch (error) {
      return false;
    }
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    return saveSettings(newSettings);
  };

  // Save one shop-wide setting (admin only — the server enforces it) and
  // broadcast it so every mounted consumer picks it up.
  const updateSharedSetting = useCallback(async (field, value) => {
    try {
      await storeSettingsApi.update(SHARED_SETTING_KEYS[field], value);
      applySharedSettings({ ...sharedRef.current, [field]: value });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update setting' };
    }
  }, [applySharedSettings]);

  // Update the global priority-time-fee toggle. Optimistic: flips local state
  // immediately, rolls back on API failure.
  const updatePriorityTimeFeeEnabled = useCallback(async (enabled) => {
    const previous = sharedRef.current.priorityTimeFeeEnabled;
    setShared({ ...sharedRef.current, priorityTimeFeeEnabled: enabled });
    const result = await updateSharedSetting('priorityTimeFeeEnabled', enabled);
    if (!result.success) setShared({ ...sharedRef.current, priorityTimeFeeEnabled: previous });
    return result;
  }, [setShared, updateSharedSetting]);

  const resetSettings = () => saveSettings(DEFAULT_SETTINGS);

  useEffect(() => {
    loadSettings();
    refreshGlobalSettings();
    refreshAppConfig();
  }, [refreshGlobalSettings, refreshAppConfig]);

  // Listen for cross-component broadcasts so all consumers stay in sync
  useEffect(() => {
    const onLocalChange = (event) => setLocalSettings(event.detail);
    const onSharedChange = (event) => setShared(event.detail);
    window.addEventListener('settingsChanged', onLocalChange);
    window.addEventListener('sharedSettingsChanged', onSharedChange);
    return () => {
      window.removeEventListener('settingsChanged', onLocalChange);
      window.removeEventListener('sharedSettingsChanged', onSharedChange);
    };
  }, [setShared]);

  // Shared values last, so a stale per-browser copy can never override them.
  const settings = { ...localSettings, ...sharedSettings };

  return {
    settings,
    loading,
    saveSettings,
    updateSetting,
    updatePriorityTimeFeeEnabled,
    updateSharedSetting,
    refreshGlobalSettings,
    resetSettings,
    loadSettings
  };
};

export default useSettings;
